// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Main App Component
// Root component that wires Providers + Layout + Page routing
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { ThemeProvider } from '@contexts/ThemeContext';
import { LangProvider, useLang } from '@contexts/LangContext';
import { AuthProvider, useAuth } from '@contexts/AuthContext';
import { useTenantData } from '@hooks/useTenantData';
import { Layout } from '@components/layout/Layout';
import { LoginPage } from '@pages/LoginPage';
import { LoadingSpinner } from '@components/ui/States';

// Page imports (will be created in Session 2)
import { DashboardPage } from '@pages/DashboardPage';
import { InsightHubPage } from '@pages/InsightHubPage';
import { MarketIntelPage } from '@pages/MarketIntelPage';
import { GrowthRoadmapPage } from '@pages/GrowthRoadmapPage';
import { FinancialRadarPage } from '@pages/FinancialRadarPage';
import { DataMappingPage } from '@pages/DataMappingPage';
import { CommandCenterPage } from '@pages/admin/CommandCenterPage';
import { UserManagementPage } from '@pages/admin/UserManagementPage';
import { TenantManagementPage } from '@pages/admin/TenantManagementPage';
import { BroadcastPage } from '@pages/admin/BroadcastPage';
import { CustomerOverridePage } from '@pages/admin/CustomerOverridePage';

// ─── Language-aware body font wrapper ───────────────────────────────────
function GlobalFontStyles() {
  const { lang } = useLang();

  useEffect(() => {
    document.body.style.fontFamily = lang === 'ar'
      ? "'Tajawal', -apple-system, sans-serif"
      : "'Outfit', -apple-system, sans-serif";
  }, [lang]);

  return null;
}

// ─── Authenticated Shell ────────────────────────────────────────────────
function AuthenticatedApp() {
  const { profile, effectiveProfile, isSuperAdmin, isImpersonating, loading: authLoading } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [isAdminView, setIsAdminView] = useState(false);

  // Determine which tenant to load data for
  const tenantId = effectiveProfile?.tenant_id;
  const { data: tenantData, loading: dataLoading, refresh } = useTenantData(tenantId);

  // Reset view when role/impersonation changes
  useEffect(() => {
    if (isSuperAdmin && !isImpersonating) {
      setIsAdminView(true);
      setActiveView('command_center');
    } else {
      setIsAdminView(false);
      setActiveView('dashboard');
    }
  }, [isSuperAdmin, isImpersonating, profile?.id]);

  // Toggle between admin and client view (for super admin preview mode)
  const toggleAdminView = () => {
    setIsAdminView(prev => {
      const next = !prev;
      setActiveView(next ? 'command_center' : 'dashboard');
      return next;
    });
  };

  if (authLoading) return <LoadingSpinner fullscreen />;

  // Render the active page based on current mode
  const renderActivePage = () => {
    // Admin pages (only when super admin AND admin view is active AND not impersonating)
    if (isSuperAdmin && isAdminView && !isImpersonating) {
      switch (activeView) {
        case 'command_center': return <CommandCenterPage />;
        case 'user_management': return <UserManagementPage />;
        case 'tenant_management': return <TenantManagementPage />;
        case 'broadcast': return <BroadcastPage />;
        case 'customer_override': return <CustomerOverridePage />;
        default: return <CommandCenterPage />;
      }
    }

    // Client pages
    if (dataLoading) return <LoadingSpinner />;
    switch (activeView) {
      case 'dashboard':
        return <DashboardPage data={tenantData} refresh={refresh} navigate={setActiveView} />;
      case 'insight_hub':
        return <InsightHubPage data={tenantData} refresh={refresh} />;
      case 'market_intel':
        return <MarketIntelPage data={tenantData} refresh={refresh} />;
      case 'growth_roadmap':
        return <GrowthRoadmapPage data={tenantData} refresh={refresh} />;
      case 'financial_radar':
        return <FinancialRadarPage data={tenantData} refresh={refresh} />;
      case 'data_mapping':
        return <DataMappingPage data={tenantData} refresh={refresh} />;
      default:
        return <DashboardPage data={tenantData} refresh={refresh} navigate={setActiveView} />;
    }
  };

  return (
    <Layout
      activeView={activeView}
      setActiveView={setActiveView}
      features={tenantData?.features || {}}
      isAdminView={isAdminView}
      onToggleAdminView={toggleAdminView}
    >
      {renderActivePage()}
    </Layout>
  );
}

// ─── Auth gate ──────────────────────────────────────────────────────────
function AppContent() {
  const { session, loading } = useAuth();

  if (loading) return <LoadingSpinner fullscreen />;
  if (!session) return <LoginPage />;

  return <AuthenticatedApp />;
}

// ─── Root App with all providers ────────────────────────────────────────
export default function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <GlobalFontStyles />
          <AppContent />
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}
